import { Icon } from '@hvac/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { recordSignIn } from '../../shared/api/queries.js';
import { useAuth } from '../../shared/auth/auth-context.js';
import { LoginForm, type LoginValues } from './login-form.js';

/**
 * The front door.
 *
 * Two panels: a statement of what this system is, and the form. The statement
 * side is not marketing, every line on it is a guarantee the codebase
 * actually enforces, which is the only kind of copy that belongs on a
 * financial product.
 */
export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });

  const handleSubmit = async (values: LoginValues): Promise<void> => {
    await auth.signIn(values.email, values.password);
    void recordSignIn();
    // `redirect` is validated as a string by the route's search schema, but it
    // is still user-controlled. Only same-origin relative paths are honoured,
    // so a crafted link cannot bounce someone to another site after sign-in.
    const target = isSafeRedirect(search.redirect) ? search.redirect : '/';
    await navigate({ to: target });
  };

  return (
    <main className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      {/* ---- the statement ---- */}
      <section className="hidden flex-col justify-between bg-gradient-to-b from-sidebar to-sidebar-deep p-12 text-white ring-1 ring-inset ring-white/[0.06] lg:flex">
        <p className="flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-[9px] bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]">
            P
          </span>
          <span className="text-sm font-semibold tracking-tight">
            Pathara <span className="text-brand-400">Finance</span>
          </span>
        </p>

        <div>
          <h1 className="max-w-md text-4xl font-semibold leading-tight tracking-tight">
            The books, run like an operating system.
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-white/70">
            Documents become records. Records carry their evidence. Every decision is explained,
            every change is remembered, and nothing reaches your accountant without a person.
          </p>
        </div>

        <ul className="flex flex-col gap-3 text-sm text-white/60">
          <li className="flex items-center gap-2.5">
            <Icon name="shield" className="text-white/40" />
            Client books are mathematically isolated, and a test suite proves it on every change.
          </li>
          <li className="flex items-center gap-2.5">
            <Icon name="check" className="text-white/40" />
            Confirmed records are immutable. History is corrected, never rewritten.
          </li>
          <li className="flex items-center gap-2.5">
            <Icon name="tax" className="text-white/40" />
            Tax is decided by legislation-backed rules. AI explains; it never decides.
          </li>
        </ul>
      </section>

      {/* ---- the form ---- */}
      <section className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <p className="mb-8 flex items-center gap-2.5 lg:hidden">
            <span className="flex size-8 items-center justify-center rounded-[9px] bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]">
              P
            </span>
            <span className="text-sm font-semibold tracking-tight text-content">
              Pathara <span className="text-brand-600">Finance</span>
            </span>
          </p>
          <h2 className="text-2xl font-semibold tracking-tight text-content">Welcome back</h2>
          <p className="mt-1 text-sm text-content-muted">
            Sign in to your organisation&rsquo;s books.
          </p>
          <div className="mt-8">
            <LoginForm onSubmit={handleSubmit} />
          </div>
          <div className="mt-4 flex justify-between text-sm">
            <Link to="/forgot-password" className="text-content-muted hover:text-content">
              Forgot your password?
            </Link>
          </div>
          <p className="mt-8 text-sm text-content-muted">
            New here?{' '}
            <Link to="/signup" className="font-medium text-brand-600 hover:underline">
              Start a free trial
            </Link>{' '}
            for your firm or your business. If somebody invited you, open the link they sent
            instead; it creates your account.
          </p>
        </div>
      </section>
    </main>
  );
}

export function isSafeRedirect(value: string | undefined): value is string {
  if (value === undefined || value.length === 0) return false;
  // A single leading slash only: `//evil.test` and `https://evil.test` are both
  // rejected, and so is anything with a scheme or a backslash.
  return value.startsWith('/') && !value.startsWith('//') && !value.includes('\\');
}
